import { Input, Spin, Select } from "antd";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "../../utils/axios";
import { carInfoVin } from "../../utils/api";
import { ToastContainer, toast } from "react-toastify";
import debounce from "lodash.debounce";
import { API_URL_MAKE, API_URL_MODEL } from "../../utils/urls";

const schema = yup.object().shape({
	modelYear: yup.string().required("Model year is required"),
	make: yup.string().required("Make is required"),
	model: yup.string().required("Model is required"),
	city: yup.string().required("Zip code is required"),
});

// Custom Icons as SVG components
const CarIcon = ({ className }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		aria-hidden="true"
	>
		<path d="M7 17a2 2 0 100-4 2 2 0 000 4zM17 17a2 2 0 100-4 2 2 0 000 4z" />
		<path
			d="M5 17H3v-6l2-5h10l4 5h2v6h-2m-8 0H9"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="M5 6l1.5-3h11L19 6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const ClipboardIcon = ({ className }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		aria-hidden="true"
	>
		<path
			d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="M9 12h6M9 16h6" strokeLinecap="round" />
	</svg>
);

const WrenchIcon = ({ className }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		aria-hidden="true"
	>
		<path
			d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const SparklesIcon = ({ className }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill="currentColor"
		aria-hidden="true"
	>
		<path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
	</svg>
);

const FormPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const carDetailFromProductPage = location.state?.carDetail;
	const [loading] = useState(false);
	const [invalidState, setIsInvalidState] = useState(false);
	const [disableVin, setDisableVin] = useState(false);
	const [disableYMM, setDisableYMM] = useState(!!carDetailFromProductPage?.vin);
	const [make, setMake] = useState([]);
	const [, setModel] = useState([]);
	const [modelData, setModeldata] = useState([]);
	const [selectedModel, setSelectedModel] = useState(
		carDetailFromProductPage?.model || "",
	);
	const [selectedMake, setSelectedMake] = useState(
		carDetailFromProductPage?.make || "",
	);

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors },
		trigger,
		watch,
	} = useForm({
		resolver: yupResolver(schema),
	});

	const onSubmit = (data) => {
		const keysToRemove = [
			"compareCars",
			"formValues",
			"salvageCars",
			"marketCars",
			"filters",
			"compareSalvage",
		];
		keysToRemove.forEach((k) => {
			localStorage.removeItem(k);
		});
		const { make, model, trim, city, modelYear } = data;
		navigate("/find_car", {
			state: {
				formInputValues: data,
				filterInput: { make, model, trim, city, year: modelYear },
			},
		});
	};

	const fetchZipDetailsRef = useRef(null);

	const fetchZipDetails = useCallback((zip) => {
		// Cancel any pending debounced call to prevent stale responses
		if (fetchZipDetailsRef.current) {
			fetchZipDetailsRef.current.cancel();
		}

		// Only validate when zip is exactly 5 digits
		if (!/^\d{5}$/.test(zip)) {
			setIsInvalidState(zip.length > 0 && zip.length >= 5);
			return;
		}

		const debouncedFetch = debounce(async () => {
			try {
				const res = await axios(`car/checkZipCode/${zip}`);
				setIsInvalidState(res?.data?.success === false);
			} catch (err) {
				console.error("Error fetching zip info:", err);
			}
		}, 500);

		fetchZipDetailsRef.current = debouncedFetch;
		debouncedFetch();
	}, []);

	const makeValidate = useCallback(async () => {
		try {
			const res = await axios.get(`${API_URL_MAKE}?popular=true`);
			const data = res?.data.map((item) => item);
			setMake((prev) => [...prev, ...data]);
			return res;
		} catch (error) {
			return error;
		}
	}, []);

	const modelValidate = useCallback(async (make_id) => {
		try {
			const res = await axios.get(`${API_URL_MODEL}/${make_id}`);
			setModeldata(res?.data);
			const data = res?.data.map((item) => item.name);
			setModel((prev) => [...prev, ...data]);
			return res;
		} catch (error) {
			return error;
		}
	}, []);

	const showToastMessage = useCallback(() => {
		toast.error("Not Found, please manually enter the Year, Make and Model", {
			position: "bottom-right",
		});
	}, []);

	const getDetailsFromVin = useCallback(
		async (vinInput) => {
			try {
				const res = await carInfoVin(vinInput);
				const data = res?.data;
				if (
					data?.data?.basic?.make &&
					data?.data?.basic?.model &&
					data?.data?.basic?.year
				) {
					setValue("make", data?.data?.basic?.make);
					setValue("model", data?.data?.basic?.model);
					setValue("modelYear", data?.data?.basic?.year.toString());
					setYearValue(data?.data?.basic?.year.toString());
					setSelectedModel(data?.data?.basic?.model);
					setSelectedMake(data?.data?.basic?.make);
				} else {
					showToastMessage();
				}
			} catch (e) {
				console.log("Error fetching vehicle data:", e);
			}
		},
		[setValue, showToastMessage],
	);

	const debouncedGetDetailsFromVin = useMemo(
		() =>
			debounce((vin) => {
				if (vin.length > 1) {
					getDetailsFromVin(vin);
				}
			}, 1000),
		[getDetailsFromVin],
	);

	const debouncedMakeValidate = useMemo(
		() => debounce(makeValidate, 500),
		[makeValidate],
	);

	const initialYears = Array.from(
		{ length: new Date().getFullYear() - 1982 + 1 },
		(_, i) => {
			const year = String(1982 + i);
			return { label: year, value: year };
		},
	).reverse();

	const [yearList, setYearList] = useState(initialYears);
	const [yearValue, setYearValue] = useState(
		carDetailFromProductPage?.year?.toString() || [],
	);
	const [error, setError] = useState(null);
	const [inputValue, setInputValue] = useState("");
	const data = watch();

	// Pre-populate form when coming from product page
	useEffect(() => {
		if (carDetailFromProductPage) {
			if (carDetailFromProductPage.vin) {
				setValue("vin", carDetailFromProductPage.vin);
				setDisableYMM(true);
			}
			if (carDetailFromProductPage.year) {
				setValue("modelYear", carDetailFromProductPage.year.toString());
				setYearValue(carDetailFromProductPage.year.toString());
			}
			if (carDetailFromProductPage.make) {
				setValue("make", carDetailFromProductPage.make);
				setSelectedMake(carDetailFromProductPage.make);
			}
			if (carDetailFromProductPage.model) {
				setValue("model", carDetailFromProductPage.model);
				setSelectedModel(carDetailFromProductPage.model);
			}
			if (carDetailFromProductPage.mileage) {
				setValue("mileage", carDetailFromProductPage.mileage);
			}
			if (carDetailFromProductPage.zipCode) {
				setValue("city", carDetailFromProductPage.zipCode);
			}
			if (carDetailFromProductPage.trim) {
				setValue("trim", carDetailFromProductPage.trim);
			}
		}
	}, [carDetailFromProductPage, setValue]);

	useEffect(() => {
		if (
			data?.make?.length > 0 ||
			data?.model?.length > 0 ||
			data?.modelYear?.length > 0
		) {
			setDisableVin(true);
		} else {
			setDisableVin(false);
		}
	}, [data]);

	const handleChange = (val) => {
		const yearStr = String(val).trim();
		const year = parseInt(yearStr, 10);
		if (!Number.isNaN(year) && year >= 1921 && year <= 2026) {
			const years = yearList.map((item) => item.value);
			if (!years.includes(yearStr)) {
				setYearList([...yearList, { label: yearStr, value: yearStr }]);
			}
			setYearValue(yearStr);
			setValue("modelYear", yearStr);
			setError(null);
			trigger("modelYear");
		} else {
			if (val === undefined) {
				setYearValue([]);
				setValue("modelYear", null);
				setError(null);
			} else {
				setYearValue(yearStr);
				setValue("modelYear", yearStr);
				setError("You have entered an invalid year. Please try again");
			}
			trigger("modelYear");
		}
	};

	const handleInputKeyDown = (e) => {
		if (e.key === "Enter") {
			handleChange(inputValue);
		}
	};

	const onhandleMakeSelect = (value) => {
		modelValidate(value);
	};

	useEffect(() => {
		makeValidate();
	}, [makeValidate]);

	const handleMakeSearch = () => {
		// Search handled by Select's filterOption
	};

	// Field configurations for left section
	const leftFields = [
		{
			label: "VIN",
			name: "vin",
			disabled: disableVin,
			placeholder: "Enter 17-character VIN",
		},
		{
			label: "Model Year",
			name: "modelYear",
			isYearDropdown: true,
			disabled: disableYMM,
			options: yearList,
			placeholder: "Select year",
		},
		{
			label: "Make",
			name: "make",
			disabled: disableYMM,
			isMakeDropdown: true,
			options: make
				? [
						{
							label: "Popular Makes",
							options: make
								?.filter((item) => item.category === "popular")
								?.map((item) => ({
									value: item?.id,
									label: item?.name,
								})),
						},
						{
							label: "All Makes",
							options: make
								?.filter((item) => item.category === "AllMakes")
								?.map((item) => ({
									value: item?.id,
									label: item?.name,
								})),
						},
					]
				: [],
			placeholder: "Select make",
		},
		{
			label: "Model",
			name: "model",
			disabled: disableYMM,
			isModelDropdown: true,
			options: modelData
				? modelData.map((item) => ({
						value: item?.id,
						label: item?.name,
					}))
				: [],
			placeholder: "Select model",
		},
		{ label: "Zip Code", name: "city", placeholder: "Enter zip code" },
		{ label: "Mileage", name: "mileage", placeholder: "Enter current mileage" },
		{
			label: "Condition",
			name: "condition",
			isSelect: true,
			options: [
				{ value: "EXCELLENT", label: "Excellent" },
				{ value: "GOOD", label: "Good" },
				{ value: "FAIR", label: "Fair" },
				{ value: "POOR", label: "Poor" },
			],
			placeholder: "Select condition",
		},
	];

	// Field configurations for right section
	const rightFields = [
		{ label: "Paint Code", name: "paintCode", placeholder: "e.g., 1G3" },
		{
			label: "Primary Damage",
			name: "primaryDamage",
			isSelect: true,
			options: [
				{ value: "ALL OVER", label: "All Over" },
				{ value: "BURN", label: "Burn" },
				{ value: "BURN - ENGINE", label: "Burn - Engine" },
				{ value: "BURN - INTERIOR", label: "Burn - Interior" },
				{ value: "FRONT END", label: "Front End" },
				{ value: "HAIL", label: "Hail" },
				{ value: "MECHANICAL", label: "Mechanical" },
				{ value: "MINOR DENT/SCRATCHES", label: "Minor Dent/Scratches" },
				{ value: "NORMAL WEAR", label: "Normal Wear" },
				{ value: "REAR END", label: "Rear End" },
				{ value: "REJECT REPAIR", label: "Reject Repair" },
				{ value: "ROLLOVER", label: "Rollover" },
				{ value: "SIDE", label: "Side" },
				{ value: "STRIPPED", label: "Stripped" },
				{ value: "TOP/ROOF", label: "Top/Roof" },
				{ value: "UNDERCARRIAGE", label: "Undercarriage" },
				{ value: "VANDALISM", label: "Vandalism" },
				{ value: "WATER/FLOOD", label: "Water/Flood" },
			],
			placeholder: "Select damage type",
		},
		{
			label: "Drivable Status",
			name: "drivableStatus",
			isSelect: true,
			options: [
				{ value: "DRIVES", label: "Drives" },
				{ value: "STARTS", label: "Starts" },
				{ value: "NON-STARTS", label: "Non-Starts" },
				{ value: "UNKNOWN", label: "Unknown" },
			],
			placeholder: "Select status",
		},
		{
			label: "Actual Cash Value (ACV)",
			name: "acv",
			placeholder: "$ Enter amount",
		},
		{
			label: "Estimated Cost of Repair",
			name: "costOfRepair",
			placeholder: "$ Enter amount",
		},
		{
			label: "Total Loss Status",
			name: "totalLossStatus",
			isSelect: true,
			options: [
				{ value: "Yes", label: "Yes" },
				{ value: "No", label: "No" },
			],
			placeholder: "Select status",
		},
	];

	const selectStyles = {
		width: "100%",
	};

	const renderField = (
		{
			label,
			name,
			disabled,
			isSelect,
			options,
			isYearDropdown,
			isMakeDropdown,
			isModelDropdown,
			placeholder,
		},
		isRightSection = false,
	) => (
		<div key={name} className="group">
			<label className="form-label" htmlFor={name}>
				{label}
				{(name === "modelYear" ||
					name === "make" ||
					name === "model" ||
					name === "city") &&
					!isRightSection && <span className="required-mark">*</span>}
			</label>
			<Controller
				name={name}
				control={control}
				defaultValue={name === "totalLossStatus" ? "Yes" : ""}
				render={({ field }) => {
					const baseSelectClass = "custom-form-select";

					if (isSelect) {
						return (
							<Select
								{...field}
								id={name}
								className={baseSelectClass}
								onChange={(val) => {
									if (label === "Make") {
										onhandleMakeSelect(val);
										make.filter((item) => item.id === val);
										setValue(
											"make",
											val
												? make.filter((item) => item.id === val)[0]?.name
												: "",
										);
									} else if (label === "Model") {
										modelData.filter((item) => item.id === val);
										setValue(
											"model",
											val
												? modelData.filter((item) => item.id === val)[0]?.name
												: "",
										);
									} else {
										field.onChange(val);
									}
								}}
								allowClear
								options={options}
								style={selectStyles}
								placeholder={placeholder}
								onSearch={(val) => {
									if (label === "Make") {
										handleMakeSearch(val);
									}
								}}
								disabled={disabled}
							/>
						);
					}

					if (isYearDropdown) {
						return (
							<Select
								id={name}
								className={baseSelectClass}
								style={selectStyles}
								placeholder={placeholder}
								value={yearValue}
								onChange={(val) => handleChange(val)}
								disabled={disabled}
								options={options}
								allowClear
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								showSearch
								onSearch={(val) => setInputValue(val)}
								onInputKeyDown={handleInputKeyDown}
							/>
						);
					}

					if (isMakeDropdown) {
						return (
							<Select
								id={name}
								className={baseSelectClass}
								style={selectStyles}
								placeholder={placeholder}
								value={selectedMake}
								onChange={(val) => {
									onhandleMakeSelect(val);
									const filtered = val
										? make.filter((item) => item.id === val)[0]?.name
										: "";
									setValue("make", filtered);
									setSelectedMake(filtered);
								}}
								disabled={disabled}
								options={options}
								allowClear
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								showSearch
							/>
						);
					}

					if (isModelDropdown) {
						return (
							<Select
								id={name}
								className={baseSelectClass}
								style={selectStyles}
								placeholder={placeholder}
								value={selectedModel}
								onChange={(val) => {
									const filtered = val
										? modelData.filter((item) => item.id === val)[0]?.name
										: "";
									setValue("model", filtered);
									setSelectedModel(filtered);
								}}
								disabled={disabled}
								options={options}
								allowClear
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								showSearch
							/>
						);
					}

					return (
						<>
							<Input
								{...field}
								id={name}
								className="custom-form-input"
								placeholder={placeholder}
								disabled={disabled}
								onChange={(e) => {
									field.onChange(e);

									if (name === "vin") {
										setDisableVin(false);
										if (e.target.value.length >= 1) {
											if (errors["modelYear"])
												errors["modelYear"].message = null;
											if (errors["city"]) errors["city"].message = null;
											if (errors["make"]) errors["make"].message = null;
											if (errors["model"]) errors["model"].message = null;

											setDisableYMM(true);
											debouncedGetDetailsFromVin(e.target.value);
										} else {
											setDisableYMM(false);
										}
									}

									if (name === "city") {
										const zipValue = e.target.value.replace(/\D/g, "");
										if (zipValue.length === 0) {
											setIsInvalidState(false);
										} else {
											fetchZipDetails(zipValue);
										}
									}

									if (name === "make") {
										debouncedMakeValidate();
									}
								}}
							/>
							{invalidState && name === "city" && (
								<p className="error-text">
									<span className="error-dot"></span>
									Invalid zip code
								</p>
							)}
						</>
					);
				}}
			/>
			{error && isYearDropdown && <p className="error-text">{error}</p>}
			{errors[name] && (
				<p className="error-text">
					<span className="error-dot"></span>
					{errors[name].message}
				</p>
			)}
		</div>
	);

	// Main component return
	return (
		<div className="report-page">
			{/* Hero Header */}
			<header className="report-hero">
				{/* Floating decorative elements */}
				<div className="hero-float hero-float-1"></div>
				<div className="hero-float hero-float-2"></div>
				<div className="hero-float hero-float-3"></div>

				<div className="hero-content">
					<div className="hero-badge">
						<SparklesIcon className="hero-badge-icon" />
						<span className="hero-badge-text">Professional Appraisal Tool</span>
					</div>

					<h1 className="hero-title">
						Vehicle <span className="hero-title-accent">Condition</span> Report
					</h1>

					<p className="hero-subtitle">
						Generate comprehensive vehicle appraisal reports with accurate
						market valuations, damage assessments, and professional
						documentation.
					</p>

					<div className="hero-stats">
						<div className="hero-stat">
							<div className="hero-stat-value">50K+</div>
							<div className="hero-stat-label">Reports Generated</div>
						</div>
						<div className="hero-stat">
							<div className="hero-stat-value">99.2%</div>
							<div className="hero-stat-label">Accuracy Rate</div>
						</div>
						<div className="hero-stat">
							<div className="hero-stat-value">2min</div>
							<div className="hero-stat-label">Avg. Generation</div>
						</div>
					</div>
				</div>
			</header>

			{/* Form Section */}
			<div className="report-form-section">
				<Spin
					spinning={loading}
					indicator={
						<LoadingOutlined style={{ fontSize: 48, color: "#38bdf8" }} spin />
					}
				>
					<form onSubmit={handleSubmit(onSubmit)} className="report-form-card">
						<div className="form-content">
							<div className="form-grid">
								{/* Left Section - Vehicle Information */}
								<div>
									<div className="section-header">
										<div className="section-icon-wrapper primary">
											<CarIcon className="section-icon" />
										</div>
										<div>
											<h2 className="section-title">Vehicle Information</h2>
											<p className="section-subtitle">
												Enter your vehicle details
											</p>
										</div>
									</div>
									<div className="fields-wrapper">
										{leftFields.map((field) => renderField(field, false))}
									</div>
								</div>

								{/* Right Section - Damage & Valuation */}
								<div>
									<div className="section-header">
										<div className="section-icon-wrapper secondary">
											<WrenchIcon className="section-icon" />
										</div>
										<div>
											<h2 className="section-title">Damage & Valuation</h2>
											<p className="section-subtitle">
												Provide condition assessment
											</p>
										</div>
									</div>
									<div className="fields-wrapper">
										{rightFields.map((field) => renderField(field, true))}
									</div>
								</div>
							</div>
						</div>

						{/* Submit Section */}
						<div className="form-footer">
							<p className="footer-note">
								<span>*</span> Required fields must be completed
							</p>
							<button
								type="submit"
								disabled={invalidState}
								className="submit-btn"
							>
								<ClipboardIcon className="submit-btn-icon" />
								Generate Report
							</button>
						</div>
					</form>
				</Spin>
			</div>

			<ToastContainer />
		</div>
	);
};

export default FormPage;
